/**
 * TabPicker Application Entry Point
 * 
 * Main entry point for the React application. Initializes the React root
 * and renders the main App component with Tailwind CSS styling.
 * 
 * This file bootstraps the entire TabPicker application for pull tabs
 * odds analysis and game management.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from "./App";
import "./index.css"; // Tailwind entrypoint
import { initSentry } from "./utils/sentry";
import { initializeStores } from "./stores";
import { updatePageTitle } from "./utils/pageTitle";

// Initialize Sentry error monitoring
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

if (sentryDsn) {
    initSentry(sentryDsn, environment);
}

// Create a client for React Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});

// Initialize Zustand stores
initializeStores();

// Set initial page title based on environment
updatePageTitle();

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <App />
        </QueryClientProvider>
    </React.StrictMode>
);
